import { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, Code, Loader2 } from 'lucide-react';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';
import { useToast } from '../../../components/common/ToastContainer';
import { formatDate } from '../../../utils/formatDate';
import { useSettingsStore } from '../../../stores/settingsStore';
import RawJsonViewer from '../../../components/common/RawJsonViewer';
import { useStaleStatusRefresh } from '../../../hooks/useStaleStatusRefresh';

interface VPPStatusProps {
  edgeId: string;
  // VSNP-700 follow-up: when the edge is online, the hook below pokes
  // the agent for a fresh status if the cached payload is older than
  // ~30s, and surfaces a "configuration is stale" banner while waiting.
  isOnline?: boolean;
}

interface VPPInterface {
  name: string;
  type: string;
  state: string;
  ip_addresses: string[];
  mac_address?: string;
  rx_packets?: number;
  tx_packets?: number;
  rx_bytes?: number;
  tx_bytes?: number;
}

interface VPPRoute {
  // UI field names
  prefix?: string;
  next_hop?: string;
  interface?: string;
  metric?: number;
  vrf?: number;
  // Agent field names (from VPP Binary API)
  destination?: string;
  dst_network?: string;
  next_hop_addr?: string;
  outgoing_interface?: string;
  weight?: number;
  preference?: number;
  vrf_id?: number;
}

interface VPPPlugin {
  name: string;
  path?: string;
  version?: string;
}

interface VPPStatusData {
  timestamp: number;
  vpp?: {
    version?: string;
    uptime?: number; // Seconds (deprecated field name)
    uptime_seconds?: number; // Seconds (new field name from agent)
    plugins?: VPPPlugin[];
    interfaces?: VPPInterface[];
    routes?: VPPRoute[];
    nat?: {
      enabled: boolean;
      sessions_count?: number;
      sessions_active?: number; // New field from agent
    };
    dhcp?: {
      enabled?: boolean;
      interface?: string;
      state?: string;
      ip_address?: string;
      leases_count?: number;
    };
  };
  data?: {
    hw_ver?: string;
    sw_ver?: string;
    man_inf?: string;
    man_ser_num?: string;
    up_time?: string;
    up_type?: number;
  };
  _metadata?: {
    id: number;
    received_at: string;
    trigger_source: string;
  };
}

const VPPStatus = ({ edgeId, isOnline = false }: VPPStatusProps) => {
  const { getDateFormatSettings } = useSettingsStore();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const dateSettings = getDateFormatSettings();
  const [statusData, setStatusData] = useState<VPPStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [activeTab, setActiveTab] = useState<'interfaces' | 'routes' | 'plugins' | 'services'>('interfaces');
  const toast = useToast();

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await edgeService.getLatestStatus(edgeId);
      setStatusData(data);
    } catch (err: any) {
      console.error('Failed to load VPP status:', err);
      if (err.message.includes('No status data found')) {
        setStatusData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // VSNP-700 follow-up: if the edge is online but the cached status
  // payload is older than 30 s (or absent), poke the agent for a fresh
  // inform and poll for the update. Banner surfaces while we wait.
  const showStaleBanner = useStaleStatusRefresh({
    edgeId,
    isOnline,
    receivedAt: statusData?._metadata?.received_at,
    refetch: fetchStatus,
  });

  const requestStatus = async () => {
    try {
      setRequesting(true);
      await edgeService.requestStatus(edgeId);
      toast.success('Status Request Sent', 'Edge device will publish status shortly. Refresh in a few seconds.');
    } catch (err: any) {
      toast.error('Request Failed', err.message || 'Failed to request status from edge');
    } finally {
      setRequesting(false);
    }
  };

  useEffect(() => {
    if (edgeId) {
      fetchStatus();
    }
  }, [edgeId, selectedTenantId]);

  const formatBytes = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return '-';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const getStateBadge = (state: string) => {
    const stateUpper = state.toUpperCase();
    if (stateUpper === 'UP' || stateUpper === 'ENABLED') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
          {state}
        </span>
      );
    }
    if (stateUpper === 'DOWN' || stateUpper === 'DISABLED') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-danger-100 text-danger-700">
          {state}
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
        {state}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* VSNP-700 follow-up: edge is online but the cached payload is
          stale (>30s old) — surface the catch-up state instead of
          silently showing stale numbers. The hook is auto-polling
          getLatestStatus while this banner is visible. */}
      {showStaleBanner && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-blue-600" aria-hidden="true" />
          <span>
            <span className="font-semibold">Configuration is stale.</span>{' '}
            Gathering confirmation from online edge now…
          </span>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {statusData?._metadata && (
            <div className="text-sm text-gray-600">
              Last updated: {formatDate(new Date(statusData._metadata.received_at), dateSettings)}
              {statusData._metadata.trigger_source && (
                <span className="ml-2 text-xs text-gray-500">
                  ({statusData._metadata.trigger_source})
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowRawJson(!showRawJson)}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <Code className="w-4 h-4" />
            <span>{showRawJson ? 'Hide' : 'Show'} Raw JSON</span>
          </button>
          <button
            onClick={requestStatus}
            disabled={requesting}
            className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${requesting ? 'animate-spin' : ''}`} />
            <span>Request Status</span>
          </button>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* VPP Info Card */}
      {statusData?.vpp && (
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">VPP Version</div>
              <div className="text-lg font-semibold text-gray-900">
                {statusData.vpp.version || 'Unknown'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">VPP Uptime</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatUptime(statusData.vpp.uptime_seconds || statusData.vpp.uptime)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Data Collection Time</div>
              <div className="text-lg font-semibold text-gray-900">
                {statusData.timestamp && statusData.timestamp > 0
                  ? formatDate(new Date(statusData.timestamp * 1000), dateSettings)
                  : 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legacy Format Warning */}
      {statusData && !statusData.vpp && statusData.data && (
        <div className="card">
          <div className="flex items-start space-x-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Legacy Status Format Detected</h3>
              <p className="text-sm text-yellow-700 mb-3">
                This edge device is publishing status confirmations in the legacy V1 format, which does not include VPP interface and routing data.
                To view VPP status, the edge agent needs to be updated to V2.
              </p>
              <div className="bg-white rounded border border-yellow-300 p-3 text-sm space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600">Hardware:</div>
                  <div className="font-mono text-gray-900">{statusData.data.hw_ver || 'Unknown'}</div>
                  <div className="text-gray-600">Software:</div>
                  <div className="font-mono text-gray-900">{statusData.data.sw_ver || 'Unknown'}</div>
                  <div className="text-gray-600">Manufacturer:</div>
                  <div className="font-mono text-gray-900">{statusData.data.man_inf || 'Unknown'}</div>
                  <div className="text-gray-600">Serial:</div>
                  <div className="font-mono text-gray-900">{statusData.data.man_ser_num || 'Unknown'}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-yellow-600">
                Last update: {statusData._metadata ? formatDate(new Date(statusData._metadata.received_at), dateSettings) : 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Raw JSON View */}
      {showRawJson && statusData && (
        <RawJsonViewer data={statusData} />
      )}

      {/* Empty VPP Data Warning */}
      {statusData?.vpp && (!statusData.vpp.interfaces || statusData.vpp.interfaces.length === 0) && (!statusData.vpp.routes || statusData.vpp.routes.length === 0) && (
        <div className="card">
          <div className="flex items-start space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">VPP Status Collection Active (No Data Yet)</h3>
              <p className="text-sm text-blue-700 mb-2">
                The edge is publishing VPP status confirmations, but interfaces and routes arrays are empty.
                This may indicate:
              </p>
              <ul className="text-sm text-blue-700 list-disc list-inside space-y-1 mb-3">
                <li>VPP is still initializing</li>
                <li>No interfaces have been configured yet</li>
                <li>Binary API queries are failing silently</li>
              </ul>
              <p className="text-xs text-blue-600">
                Try requesting a new status update or check edge logs for VPP status collection errors.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && !statusData && (
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No VPP Status Data Available</h3>
            <p className="text-gray-600 mb-4">
              No status confirmations have been received from this edge device yet.
            </p>
            <button
              onClick={requestStatus}
              disabled={requesting}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${requesting ? 'animate-spin' : ''}`} />
              <span>Request Status Now</span>
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!showRawJson && statusData?.vpp && (
        <>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveTab('interfaces')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'interfaces'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Interfaces ({statusData.vpp.interfaces?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('routes')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'routes'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Routes ({statusData.vpp.routes?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('plugins')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'plugins'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Plugins ({statusData.vpp.plugins?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'services'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Services
            </button>
          </div>

          {/* Interfaces Tab */}
          {activeTab === 'interfaces' && (
            <div className="card overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">VPP Interfaces</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        State
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        IP Addresses
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        MAC Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        RX Packets
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        TX Packets
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        RX Bytes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        TX Bytes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statusData.vpp.interfaces && statusData.vpp.interfaces.length > 0 ? (
                      statusData.vpp.interfaces.map((iface, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {iface.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {iface.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStateBadge(iface.state)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {iface.ip_addresses && iface.ip_addresses.length > 0 ? (
                              <div className="space-y-1">
                                {iface.ip_addresses.map((ip, i) => (
                                  <div key={i} className="font-mono text-xs">
                                    {ip}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {iface.mac_address || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {iface.rx_packets?.toLocaleString() || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {iface.tx_packets?.toLocaleString() || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatBytes(iface.rx_bytes)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatBytes(iface.tx_bytes)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                          No interfaces data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Routes Tab */}
          {activeTab === 'routes' && (
            <div className="card overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">VPP Routing Table</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Destination Prefix
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Next Hop
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Interface
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        VRF
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Metric
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statusData.vpp.routes && statusData.vpp.routes.length > 0 ? (
                      [...statusData.vpp.routes]
                        .sort((a, b) => {
                          // Sort by destination prefix
                          const destA = a.prefix || a.destination || a.dst_network || '';
                          const destB = b.prefix || b.destination || b.dst_network || '';
                          // Parse IP and sort numerically
                          const parseIP = (cidr: string) => {
                            const [ip] = cidr.split('/');
                            const parts = ip.split('.').map(Number);
                            return parts.reduce((acc, p, i) => acc + p * Math.pow(256, 3 - i), 0);
                          };
                          try {
                            return parseIP(destA) - parseIP(destB);
                          } catch {
                            return destA.localeCompare(destB);
                          }
                        })
                        .map((route, idx) => {
                        // Handle both UI field names and agent field names
                        const destination = route.prefix || route.destination || route.dst_network || '-';
                        const nextHop = route.next_hop || route.next_hop_addr || '-';
                        const iface = route.interface || route.outgoing_interface || '-';
                        const vrf = route.vrf ?? route.vrf_id ?? 0;
                        const metric = route.metric || route.preference || route.weight;

                        return (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {destination}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {nextHop}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {iface}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {vrf}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {metric || '-'}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                          No routes data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Plugins Tab */}
          {activeTab === 'plugins' && (
            <div className="card overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">VPP Plugins</h3>
              {statusData.vpp.plugins && statusData.vpp.plugins.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Plugin Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Version
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Path
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {statusData.vpp.plugins.map((plugin, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {plugin.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {plugin.version || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                            {plugin.path || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">No plugins data available</p>
                  <p className="text-sm text-gray-400">Plugin enumeration requires agent v2 with CLI support</p>
                </div>
              )}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-4">
              {/* NAT Status */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">NAT Service</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    {statusData.vpp.nat?.enabled ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <span className="text-sm font-medium text-gray-900">Enabled</span>
                      </>
                    ) : statusData.vpp.nat?.sessions_active !== undefined ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">Disabled</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm font-medium text-gray-500">Not Collected</span>
                      </>
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Active Sessions</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {statusData.vpp.nat?.sessions_active !== undefined
                        ? (statusData.vpp.nat?.sessions_active ?? statusData.vpp.nat?.sessions_count ?? 0).toLocaleString()
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* DHCP Client Status */}
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">DHCP Client Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3">
                    {statusData.vpp.dhcp?.state === 'bound' ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <span className="text-sm font-medium text-gray-900">Bound</span>
                      </>
                    ) : statusData.vpp.dhcp?.state ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm font-medium text-gray-900 capitalize">{statusData.vpp.dhcp.state}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">Not Active</span>
                      </>
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Interface</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {statusData.vpp.dhcp?.interface || '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Assigned IP</div>
                    <div className="text-lg font-semibold text-gray-900 font-mono">
                      {statusData.vpp.dhcp?.ip_address || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VPPStatus;
